import React from "react";
import { UserPhrase } from "@shared_types";
import { FlagIcon } from "@/ui";
import { FaTrash, FaTimesCircle, FaCheckCircle, FaPlus } from "react-icons/fa";
import ToggleButton from "../Buttons/ToggleButton";

interface PhraseCardProps {
  phrase: UserPhrase;
  viewType: "known" | "learning";
  effectiveStatus: "known" | "learning";
  deleteConfirmId: number | null;
  onStatusChange: (phrase: UserPhrase, newStatus: "known" | "learning") => void;
  onDeleteClick: (phrase: UserPhrase) => void;
  onCancelDelete: () => void;
  lastPhraseRef?: React.Ref<HTMLDivElement>;
}

const GREEN = "text-[var(--color-green)]";
// iOS StatusToggleButton renders the unselected/action state in muted grey
// (icon globalColors.grey, text "muted"). Mirror that — matches WordCard.
const MUTED = "text-[var(--color-grey-other)]";

const PhraseCard: React.FC<PhraseCardProps> = ({
  phrase,
  viewType,
  effectiveStatus,
  deleteConfirmId,
  onStatusChange,
  onDeleteClick,
  onCancelDelete,
  lastPhraseRef,
}) => {
  // Mirror iOS StatusToggleButton semantics (see WordCard).
  let activeIcon: React.ReactNode;
  let inactiveIcon: React.ReactNode;
  let activeText: string;
  let inactiveText: string;
  let activeColor: string;
  let inactiveColor: string;

  if (viewType === "learning") {
    inactiveText = "I know this";
    inactiveIcon = <FaPlus className="w-3 h-3" />;
    inactiveColor = MUTED;
    activeText = "known";
    activeIcon = <FaCheckCircle className="w-3 h-3" />;
    activeColor = GREEN;
  } else {
    activeText = "to learning";
    activeIcon = <FaPlus className="w-3 h-3" />;
    activeColor = MUTED;
    inactiveText = "learning";
    inactiveIcon = <FaCheckCircle className="w-3 h-3" />;
    inactiveColor = GREEN;
  }

  return (
    <div
      ref={lastPhraseRef}
      className="flex flex-col p-4 bg-white rounded-[32px] gap-2 min-h-[94px] shadow-[0_1px_8px_rgba(0,0,0,0.05)]"
    >
      {/* Spanish phrase row */}
      <div className="flex items-start w-full">
        <FlagIcon
          countryCode="ES"
          size="small"
          className="mr-2 mt-1 flex-shrink-0"
        />
        <div className="font-bold text-base break-words text-left">
          {phrase.text}
        </div>
      </div>

      {/* English translation row */}
      <div className="flex items-start text-[var(--color-grey-other)]">
        <FlagIcon
          countryCode="UK"
          size="small"
          className="mr-2 mt-1 flex-shrink-0"
        />
        <div className="break-words text-left text-base">
          {phrase.translation}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-1 justify-end mt-2">
        <ToggleButton
          isActive={effectiveStatus === "known"}
          activeText={activeText}
          inactiveText={inactiveText}
          activeIcon={activeIcon}
          inactiveIcon={inactiveIcon}
          disableAnimation={true}
          activeColor={activeColor}
          inactiveColor={inactiveColor}
          onClick={() =>
            onStatusChange(
              phrase,
              effectiveStatus === "known" ? "learning" : "known"
            )
          }
          className="text-xs px-2 py-1 rounded-full transition-colors"
        />
        {deleteConfirmId === phrase.id ? (
          <div className="flex gap-1">
            <button
              onClick={() => onDeleteClick(phrase)}
              className="text-xs text-white bg-[var(--color-red)] border border-[var(--color-red)] px-2 py-[2px] rounded-full transition-colors flex items-center gap-1"
            >
              <FaCheckCircle className="w-3 h-3" />
            </button>
            <button
              onClick={onCancelDelete}
              className="text-xs border border-[var(--color-grey-light)] px-2 py-[2px] rounded-full transition-colors flex items-center gap-1"
            >
              <FaTimesCircle className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => onDeleteClick(phrase)}
            className="text-xs border border-[var(--color-grey-light)] px-2 py-1 rounded-full transition-colors flex items-center gap-1"
          >
            <FaTrash className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PhraseCard;
